import * as api from "@/api";
import QueryDataManager from "@/classes/query-data-manager";
import Draggable from "@/components/draggable";
import Shortcut from "@/components/shortcut";
import styles from "@/components/shortcut.module.css";
import * as signals from "@/signals";
import { DialogIcon, FolderInfo } from "@/types";
import {
	handleKeyboardClick,
	handleLongPress,
	showDialog,
	showMenu,
} from "@/utils";
import { faPenToSquare, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import { JSX } from "react";
import { LongPressEventType, useLongPress } from "use-long-press";

interface ShortcutFolderProps {
	dragId: string;
	folder: FolderInfo;
}

function ShortcutFolder({ dragId, folder }: ShortcutFolderProps): JSX.Element {
	const { children, folderName, id } = folder;

	const queryClient = useQueryClient();

	const handleClick = (event?: React.MouseEvent<HTMLDivElement>): void => {
		event?.stopPropagation();
		if (signals.isDragging.value) {
			return;
		}
		signals.currentFolder.value = folder;
	};

	const handleContextMenu = (
		event: MouseEvent | React.MouseEvent<HTMLDivElement>,
	): void => {
		if (!id || signals.isDragging.value) {
			return;
		}
		showMenu(
			event,
			[
				{
					content: {
						icon: faPenToSquare,
						text: t("edit"),
					},
					onClick: (event?: React.MouseEvent<Element>): void => {
						signals.editingFolder.value = folder;
						signals.renameShortcutFolderPopup.value = {
							isShown: true,
							position: {
								left:
									(event
										? event.clientX
										: window.innerWidth / 2) - 150,
								top:
									(event
										? event.clientY
										: window.innerHeight / 2) - 140,
							},
						};
					},
					when: true,
				},
				{
					content: {
						icon: faTrashCan,
						isDangerous: true,
						text: t("delete"),
					},
					onClick: async (): Promise<void> => {
						await showDialog(t("confirmDeleteThisFolder"), {
							icon: DialogIcon.WARNING,
							showCancel: true,
							title: t("confirm"),
						});
						signals.isLoadingShown.value = true;
						try {
							await new QueryDataManager(
								queryClient,
							).deleteBookmarkFolder(id);
						} catch (error) {
							void api.handleApiError(error);
						} finally {
							signals.isLoadingShown.value = false;
						}
					},
					when: true,
				},
			],
			{ shouldBeLighterInDarkTheme: true },
		);
	};

	const longPress = useLongPress(handleLongPress(handleContextMenu), {
		detect: LongPressEventType.Touch,
		threshold: 500,
	});

	const childElements = children.map((child) => (
		<Shortcut
			dragId={dragId}
			isDraggable={false}
			isInDock={false}
			isInFolder={true}
			item={child}
			key={child.id}
		/>
	));

	return (
		<Draggable
			dragId={dragId}
			isDraggable={true}
		>
			<div
				aria-label={folderName}
				className={clsx(
					styles["shortcut"],
					styles["folder"],
					signals.isDragging.value && styles["dragging"],
				)}
				onClick={handleClick}
				onContextMenu={handleContextMenu}
				onKeyDown={handleKeyboardClick(handleClick)}
				role="button"
				tabIndex={0}
				title={folderName}
				{...longPress()}
			>
				{childElements}
				<div className={styles["shortcut-title"]}>{folderName}</div>
			</div>
		</Draggable>
	);
}

export default ShortcutFolder;
